/**
 * Generic, serialisable summary of a {@link MaterializedDataset} for the
 * LLM transcript. Pure and dataset-source-agnostic — any tool that holds
 * a materialised dataset can summarise it here and render the result with
 * `<DatasetPreview>` (the JSX → markdown reconciler turns it into GFM
 * tables the model reads).
 *
 * The summary stores FACTS only — counts, raw {@link ColumnStats},
 * frequencies, the fetch limit, complete row cells. All prose (stat
 * lines, unit-labelled types, signal sentences) is generated at render
 * time by `<DatasetPreview>`'s self-deriving components, so presentation
 * improvements apply retroactively to persisted threads.
 *
 * The output is intentionally JSON-safe (cells pre-stringified, temporal
 * stats in epoch-ms, units as entries arrays) because the AI SDK
 * tool-output envelope is a `JSONValue` contract — the UIMessage chunk
 * stream and message validation assume JSON values, regardless of the
 * structured-clone IndexedDB persistence being more permissive.
 */

import { displayCell } from '#core/lib/expr/runtime/cell-key.ts';
import {
  type ColumnType,
  describeColumnType,
  isNumericColumn,
  isTemporalColumn,
  unwrapNullable,
} from '#core/lib/expr/schema/types.ts';
import { type Cell, type MaterializedDataset, numericScalarOf, unitOfCell } from '#core/lib/expr/value.ts';
import { computeTimeseriesInsights } from '#core/lib/timeseries/insights/compute-insights.ts';
import type { TimeseriesInsights, TimeseriesPoint } from '#core/lib/timeseries/insights/types.ts';
import { formatUnit } from '#core/lib/units/registry.ts';

import {
  roundForDisplay,
  type SerializableUnit,
  serializableUnitOf,
} from '#plugin-chat/components/tools/helpers/format-with-unit.ts';
import { type ColumnStats, computeAutoStats } from '#plugin-chat/lib/tool/auto-stats.ts';
import type { RecoveryEntry } from '#plugin-chat/lib/tool/recovery.ts';

/** Per-column profile facts rendered as a row of the column table. */
export type DatasetColumnSummary = Readonly<{
  name: string;
  /**
   * Precise engine column kind (`describeColumnType`) — the factual
   * label only. The unit suffix and the shared magnitude-formatter rung
   * are derived at render time from `valueType` + `stats`.
   */
  type: string;
  /**
   * The column's declared unit as a JSON-round-trippable dimension vector
   * (`[baseSlug, exponent]` entries), or `undefined` when unit-less. Carries
   * exactly what the core UoM formatters need so numeric stat and insight
   * magnitudes render with their unit of measure — see {@link SerializableUnit}.
   * Stored as entries (not the engine `ColumnType.unit` `Map`) because the
   * tool-output envelope is a `JSONValue` contract, which flattens a `Map`.
   */
  valueType?: SerializableUnit;
  nullCount: number;
  /**
   * Distinct display-label count (labels via `displayCell`, numbers
   * pre-rounded by `roundForDisplay` — so near-identical floats share one
   * identity and `distribution.length === distinctCount` whenever a
   * distribution is attached). Emitted only for kinds where it is meaningful
   * (the {@link canShowDistribution} kinds: scalar leaves, struct, array).
   * Omitted for `nested-dataset` / `timeseries` handles, whose per-cell lazy
   * datasets have no enumerable scalar identity (the count would be noise).
   */
  distinctCount?: number;
  /**
   * Raw type-aware column stats (numeric range/mean, date range in
   * epoch-ms, struct member coverage), or `undefined` when the profiler
   * produced none — string-like columns carry no stats at all; their
   * facts are `nullCount` / `distinctCount` / `distribution`. Persisted
   * as facts; the stat prose is rendered from them at render time.
   */
  stats?: ColumnStats;
  /**
   * Frequency distribution of distinct values, descending by count. Set
   * on any enumerable column ({@link canShowDistribution} — the primitive
   * leaves plus struct / array, which bucket structurally via
   * `displayCell`) with `distinctCount` ≤
   * {@link MAX_ENUM_DISTINCT}, and only on the first
   * {@link MAX_DISTRIBUTION_COLUMNS} such columns in schema order —
   * beyond that the agent should `(aggregate (n (count)) :by [<col>])`
   * explicitly. Answers any "what values appear" question directly.
   * Numeric cells are rounded via `roundForDisplay`
   * so two near-identical floats share a bucket; the column's `stats`
   * carry full-precision min/max/mean.
   */
  distribution?: readonly FrequencyBucket[];
  /**
   * Timeseries insights for a numeric signal column, attached only when the
   * dataset has a temporal axis: overall trend, local phases, level shifts,
   * anomalies, and seasonality — each carrying a plain-language label.
   * Absent for non-numeric columns and non-timeseries datasets.
   */
  insights?: TimeseriesInsights;
  /**
   * Unit distribution across the column's boxed cells (display symbol →
   * count, descending), stored only when the cells DISAGREE (2+ distinct
   * symbols). The fact behind the derived `mixed-units` signal — a
   * unit-homogeneous or unitless column omits it entirely.
   */
  unitDistribution?: readonly Readonly<{ symbol: string; count: number }>[];
}>;

/**
 * Serialisable dataset summary consumed by `<DatasetPreview>` — FACTS
 * only; prose and signals derive at render time (module doc has the full
 * rationale).
 */
export type DatasetSummary = Readonly<{
  rowCount: number;
  columns: readonly DatasetColumnSummary[];
  /**
   * The source-fetch cap applied when the dataset was materialised.
   * `rowCount === limit` means the result may be truncated (derived as
   * a `truncated` signal). Absent when no source cap applied.
   */
  limit?: number;
  /**
   * The COMPLETE result's cells, row-major, aligned with `columns` —
   * pre-stringified via the same labelling as `distribution` buckets,
   * long cells truncated at {@link INLINE_MAX_CELL_CHARS}, nulls as ''.
   * Captured only when the result is provably complete (not truncated
   * at a limit) and small (see {@link INLINE_CELL_BUDGET}) — so a
   * renderer inlining these rows never shows a *sample* the model could
   * mistake for the full dataset.
   */
  rows?: readonly (readonly string[])[];
  /**
   * Ops of a pure projection chain (rename/columns/select over a bound
   * dataset) — the fact behind the derived `inlineable-projection`
   * signal. Set by the transform tool; absent elsewhere.
   */
  inlineableOps?: readonly string[];
  /**
   * Per-column distinct-values / range hints, populated only when the
   * result is empty OR a column came back fully null. Tells the agent
   * what values actually exist at the source so it can correct a
   * hallucinated filter. Populated by the per-tool recovery fetcher;
   * empty / absent on healthy results.
   */
  recoveryContext?: readonly RecoveryEntry[];
}>;

export type SummarizeDatasetOptions = Readonly<{
  /** Column names to exclude from the profile (e.g. synthetic ids). */
  omitColumns?: readonly string[];
  /**
   * Limit applied to the underlying source fetch. When `rowCount ===
   * limit`, a `truncated` signal is emitted so the agent knows the
   * result may not be the full domain. Omit for transforms / sources
   * that have no inherent row cap.
   */
  limit?: number;
}>;

/**
 * Distinct-value count above which the per-column distribution is
 * dropped — full policy on the {@link DatasetColumnSummary.distribution}
 * field doc.
 */
export const MAX_ENUM_DISTINCT = 20;

/** Soft cap on the number of columns carrying a `distribution`
 * payload in one summary, bounding transcript bloat on wide tables. */
const MAX_DISTRIBUTION_COLUMNS = 6;
/**
 * Cell budget (rows × columns) under which a complete result's rows are
 * captured on the summary for inline rendering — small enough that the
 * data itself is cheaper and strictly more useful than a stats profile.
 */
const INLINE_CELL_BUDGET = 100;
/**
 * Extended cell budget applied when every distribution-capable column is
 * all-unique — a frequency histogram would just repeat the data one
 * bucket per row, so inlining stays the better deal for longer.
 */
const INLINE_ALL_UNIQUE_CELL_BUDGET = 200;
/**
 * Per-cell character cap for inlined rows. The cell budget counts cells,
 * not tokens — one prose/JSON cell could dump pages into the transcript,
 * so oversized values are truncated with an ellipsis.
 */
const INLINE_MAX_CELL_CHARS = 120;
/**
 * The set of {@link LeafKind}s whose cells serialise to a single
 * canonical scalar identity for the recovery distinct-values payload.
 * The recovery pipeline can only fetch + display scalar leaves
 * (string / number / boolean / date / timestamp); struct or array
 * cells aren't reducible to a single recovery hint and are skipped.
 *
 * SSOT for `recovery.ts`'s `classifyForRecovery` guard.
 */
export const isPrimitiveLeafKind = (kind: ColumnType['kind']): boolean =>
  kind === 'string' ||
  kind === 'boolean' ||
  kind === 'number' ||
  kind === 'integer' ||
  kind === 'timestamp' ||
  kind === 'date';

/**
 * The set of column kinds that can carry a distribution histogram
 * in the LLM-facing dataset summary. Includes the primitive leaves
 * AND struct / array kinds (those bucket structurally via
 * `displayCell` — `{externalId:X}`-style readable labels). Excludes
 * `nested-dataset` and `timeseries` (no useful per-cell label) and
 * `null` / `unknown` (no enumerable values).
 *
 * Distinct from {@link isPrimitiveLeafKind} because the recovery
 * pipeline and the distribution renderer have different contracts:
 * recovery needs a scalar to fetch, the histogram only needs a
 * structurally-stable label.
 */
const canShowDistribution = (kind: ColumnType['kind']): boolean =>
  isPrimitiveLeafKind(kind) || kind === 'struct' || kind === 'array';

/** A distinct display label and how many cells carry it. */
export type FrequencyBucket = Readonly<{ value: string; count: number }>;

/**
 * Count cells per label (cells labelled `undefined` are skipped), sorted
 * desc by count — the one fold behind every frequency fact on the summary.
 */
const tally = (cells: readonly Cell[], labelOf: (cell: Cell) => string | undefined): readonly FrequencyBucket[] => {
  const counts = cells.reduce<Map<string, number>>((acc, cell) => {
    const label = labelOf(cell);
    return label === undefined ? acc : acc.set(label, (acc.get(label) ?? 0) + 1);
  }, new Map());
  return Array.from(counts, ([value, count]) => ({ value, count })).sort((a, b) => b.count - a.count);
};
/**
 * Distinct-value frequency map for a column, sorted desc by count.
 * SSOT for "what values appear in this column" — used by
 * {@link summarizeDataset}'s low-cardinality distribution and by
 * the recovery path's distinct-values payload.
 *
 * Bucket labels go through the core `displayCell` SSOT, so struct
 * cells (`{externalId:X}`) and array cells (`[a,b]`) bucket
 * structurally with readable labels. Numbers are pre-rounded via
 * `roundForDisplay` so 1.000001 / 1.000002 share a bucket (the
 * column's `stats` still carry full-precision min/max/mean
 * for that detail). Null / undefined cells are skipped — they're
 * counted separately as `nullCount` in the column summary.
 */
export const computeColumnFrequencies = (cells: readonly Cell[]): readonly FrequencyBucket[] =>
  tally(cells, (cell) => (cell === null || cell === undefined ? undefined : cellLabel(cell)));

/**
 * Canonical display label for a present cell — numbers pre-rounded via
 * `roundForDisplay` so near-identical floats share a label, everything
 * else through the core `displayCell` SSOT. Shared between distribution
 * buckets and inlined row cells so the two renderings never disagree.
 */
const cellLabel = (cell: Cell): string => {
  const n = numericScalarOf(cell);
  return n === undefined ? displayCell(cell) : String(roundForDisplay(n));
};

/** {@link cellLabel} for an inlined row cell: nulls as '', long values truncated. */
const inlineCellLabel = (cell: Cell | undefined): string => {
  if (cell === null || cell === undefined) {
    return '';
  }
  const label = cellLabel(cell);
  return label.length > INLINE_MAX_CELL_CHARS ? `${label.slice(0, INLINE_MAX_CELL_CHARS)}…` : label;
};

/** Soft cap on the number of columns carrying timeseries `insights`, to bound transcript size. */
const MAX_INSIGHT_COLUMNS = 12;

/** Minimum time-aligned points before insights are worth computing for a signal column. */
const MIN_POINTS_FOR_INSIGHTS = 8;

/** Epoch-ms from a temporal cell (a `Date`) or a numeric cell (plain or unit-boxed); null for anything else. */
function toEpochMs(cell: Cell | undefined): number | null {
  if (cell instanceof Date) {
    return cell.getTime();
  }
  const n = numericScalarOf(cell);
  return n !== undefined && Number.isFinite(n) ? n : null;
}

/**
 * Insights for a numeric signal column, zipping its values against the temporal
 * axis. Returns null when too few points align to be worth reporting.
 */
function columnInsights(timeCells: readonly Cell[], valueCells: readonly Cell[]): TimeseriesInsights | null {
  const points = valueCells.reduce<TimeseriesPoint[]>((acc, cell, i) => {
    const timestamp = toEpochMs(timeCells[i]);
    const value = numericScalarOf(cell);
    if (timestamp !== null && value !== undefined && Number.isFinite(value)) {
      acc.push({ timestamp, value });
    }
    return acc;
  }, []);
  return points.length >= MIN_POINTS_FOR_INSIGHTS ? computeTimeseriesInsights(points) : null;
}

/**
 * Unit distribution across a column's boxed cells: display symbol → count,
 * descending — or `undefined` unless the boxed cells DISAGREE (2+ distinct
 * symbols). A unit-homogeneous or entirely unitless column stores nothing.
 * Plain (unboxed) numbers don't participate: boxed alongside plain is the
 * engine's documented unit-drop case, not a conflict. The stored fact
 * behind the derived `mixed-units` signal.
 */
function unitDistributionOf(cells: readonly Cell[]): DatasetColumnSummary['unitDistribution'] {
  const buckets = tally(cells, (cell) => {
    const unit = unitOfCell(cell);
    return unit === undefined ? undefined : formatUnit(unit);
  });
  return buckets.length < 2 ? undefined : buckets.map(({ value, count }) => ({ symbol: value, count }));
}

/**
 * Summarise a {@link MaterializedDataset} into a serialisable
 * {@link DatasetSummary} — per-column fact profile plus the facts
 * `<DatasetPreview>` derives from at render time (the module doc has the
 * facts-only contract; each field's policy lives on its doc). Pure — no
 * I/O, no React, no prose. Row cells are captured ONLY when they are
 * provably the complete result (no truncating limit) and within the
 * inline cell budget — the model must never see a row *sample* it could
 * quote as if it were the dataset. Tools that need recovery context for
 * an empty / all-null result extend the returned summary with
 * `recoveryContext` after calling their per-tool {@link RecoveryFetcher}.
 *
 * Optional facts are assigned as possibly-`undefined` fields — the
 * JSONValue envelope drops them on serialisation, and every consumer
 * treats an `undefined` field as absent.
 */
export function summarizeDataset(ds: MaterializedDataset, opts: SummarizeDatasetOptions = {}): DatasetSummary {
  const omit = new Set(opts.omitColumns ?? []);
  // All columns are row-aligned (a MaterializedDataset constructor invariant),
  // so any column's length is the row count.
  const rowCount = Object.values(ds.columns)[0]?.length ?? 0;
  const colNames = Array.from(ds.schema.keys()).filter((name) => !omit.has(name));
  const statsByCol = computeAutoStats(Object.fromEntries(colNames.map((name) => [name, ds.columns[name] ?? []])));

  // The first temporal column is the shared time axis for column insights.
  const timeColumn = Array.from(ds.schema).find(([, type]) => isTemporalColumn(type))?.[0] ?? null;
  const timeCells = timeColumn ? (ds.columns[timeColumn] ?? []) : [];

  // Fold over the columns in declaration order, carrying the two soft caps
  // as accumulator state so each column consumes (and decrements) a cap in
  // exact iteration order. `distLeft`/`insightsLeft` are read before the
  // column is built and decremented only when the column actually carries a
  // distribution / insights.
  const { columns } = colNames.reduce<{
    columns: DatasetColumnSummary[];
    distLeft: number;
    insightsLeft: number;
  }>(
    (acc, name) => {
      const cells = ds.columns[name] ?? [];
      const type = ds.schema.get(name) as ColumnType;
      const innerKind = unwrapNullable(type).kind;
      // One tally per enumerable column is both the distinct count and the
      // distribution buckets — a single notion of distinctness (see the
      // `distinctCount` field doc). Non-enumerable kinds skip it entirely.
      const buckets = canShowDistribution(innerKind) ? computeColumnFrequencies(cells) : undefined;
      const distinctCount = buckets?.length;
      // `distinctCount > 0` excludes empty results AND all-null columns in
      // one check, leaving the recovery context as the sole SSOT for
      // distinct values in those cases (full policy: `distribution` doc).
      const showDistribution =
        acc.distLeft > 0 && distinctCount !== undefined && distinctCount > 0 && distinctCount <= MAX_ENUM_DISTINCT;
      const insights =
        timeColumn && isNumericColumn(type) && acc.insightsLeft > 0 ? columnInsights(timeCells, cells) : null;
      const column: DatasetColumnSummary = {
        name,
        type: describeColumnType(type),
        valueType: serializableUnitOf(type),
        nullCount: cells.filter((c) => c === null || c === undefined).length,
        distinctCount,
        stats: statsByCol[name],
        distribution: showDistribution ? buckets : undefined,
        insights: insights ?? undefined,
        unitDistribution: unitDistributionOf(cells),
      };
      return {
        columns: [...acc.columns, column],
        distLeft: showDistribution ? acc.distLeft - 1 : acc.distLeft,
        insightsLeft: insights ? acc.insightsLeft - 1 : acc.insightsLeft,
      };
    },
    { columns: [], distLeft: MAX_DISTRIBUTION_COLUMNS, insightsLeft: MAX_INSIGHT_COLUMNS },
  );

  // Inline the actual rows only when they are provably the COMPLETE
  // result (a limit-capped result may be a sample) and small enough that
  // the data is cheaper than a profile. When every distribution-capable
  // column is all-unique a histogram would repeat the data one bucket per
  // row, so the budget stretches.
  const cellCount = rowCount * columns.length;
  const enumerable = columns.filter((c) => c.distinctCount !== undefined);
  const allUnique = enumerable.length > 0 && enumerable.every((c) => c.distinctCount === rowCount - c.nullCount);
  const budget = allUnique ? INLINE_ALL_UNIQUE_CELL_BUDGET : INLINE_CELL_BUDGET;
  const complete = rowCount > 0 && (opts.limit === undefined || rowCount < opts.limit);

  return {
    rowCount,
    columns,
    limit: opts.limit,
    rows:
      complete && cellCount > 0 && cellCount <= budget
        ? Array.from({ length: rowCount }, (_, i) => colNames.map((name) => inlineCellLabel(ds.columns[name]?.[i])))
        : undefined,
  };
}
